import React from 'react';
import Icon from '../../../components/AppIcon';

const SEOContent = ({ language }) => {
  const content = {
    en: {
      title: "Free Financial Calculators for Smart Money Management",
      sections: [
        {
          heading: "Why Use Our Financial Calculators?",
          icon: "Target",
          text: "Our calculators provide instant, accurate answers to critical financial questions. Whether you're planning your budget or managing debt, get clear insights in seconds with our easy-to-use tools designed for real-world financial planning."
        },
        {
          heading: "Money Duration Calculator Benefits",
          icon: "Clock",
          text: "Understand exactly how long your savings will last based on your current expenses. Get visual status indicators showing whether you're in a safe, warning, or critical financial position. Plan ahead with confidence knowing your exact financial runway."
        },
        {
          heading: "Debt Freedom Calculator Advantages",
          icon: "TrendingDown",
          text: "Calculate your exact debt-free date and see your progress status. Know whether you're on track, struggling, or need to adjust your payment strategy. Take control of your debt with clear, actionable timelines."
        },
        {
          heading: "Localized for Your Region",
          icon: "Globe",
          text: "All calculations automatically adapt to your local currency, number formatting, and date conventions. Get results that make sense for your specific country and city, with regional financial context included."
        }
      ]
    },
    es: {
      title: "Calculadoras Financieras Gratuitas para Gestión Inteligente del Dinero",
      sections: [
        {
          heading: "¿Por Qué Usar Nuestras Calculadoras Financieras?",
          icon: "Target",
          text: "Nuestras calculadoras proporcionan respuestas instantáneas y precisas a preguntas financieras críticas. Ya sea que estés planificando tu presupuesto o gestionando deudas, obtén información clara en segundos con nuestras herramientas fáciles de usar diseñadas para la planificación financiera del mundo real."
        },
        {
          heading: "Beneficios de la Calculadora de Duración del Dinero",
          icon: "Clock",
          text: "Comprende exactamente cuánto durarán tus ahorros según tus gastos actuales. Obtén indicadores de estado visuales que muestran si estás en una posición financiera segura, de advertencia o crítica. Planifica con confianza conociendo tu margen financiero exacto."
        },
        {
          heading: "Ventajas de la Calculadora de Libertad de Deudas",
          icon: "TrendingDown",
          text: "Calcula tu fecha exacta libre de deudas y ve tu estado de progreso. Sabe si estás en camino, luchando o necesitas ajustar tu estrategia de pago. Toma el control de tu deuda con cronogramas claros y accionables."
        },
        {
          heading: "Localizado para Tu Región",
          icon: "Globe",
          text: "Todos los cálculos se adaptan automáticamente a tu moneda local, formato de números y convenciones de fecha. Obtén resultados que tienen sentido para tu país y ciudad específicos, con contexto financiero regional incluido."
        }
      ]
    },
    pt: {
      title: "Calculadoras Financeiras Gratuitas para Gestão Inteligente de Dinheiro",
      sections: [
        {
          heading: "Por Que Usar Nossas Calculadoras Financeiras?",
          icon: "Target",
          text: "Nossas calculadoras fornecem respostas instantâneas e precisas para questões financeiras críticas. Seja planejando seu orçamento ou gerenciando dívidas, obtenha insights claros em segundos com nossas ferramentas fáceis de usar projetadas para planejamento financeiro do mundo real."
        },
        {
          heading: "Benefícios da Calculadora de Duração do Dinheiro",
          icon: "Clock",
          text: "Entenda exatamente quanto tempo suas economias durarão com base em suas despesas atuais. Obtenha indicadores de status visuais mostrando se você está em uma posição financeira segura, de aviso ou crítica. Planeje com confiança conhecendo sua margem financeira exata."
        },
        {
          heading: "Vantagens da Calculadora de Liberdade de Dívidas",
          icon: "TrendingDown",
          text: "Calcule sua data exata livre de dívidas e veja seu status de progresso. Saiba se você está no caminho certo, lutando ou precisa ajustar sua estratégia de pagamento. Assuma o controle de sua dívida com cronogramas claros e acionáveis."
        },
        {
          heading: "Localizado para Sua Região",
          icon: "Globe",
          text: "Todos os cálculos se adaptam automaticamente à sua moeda local, formatação de números e convenções de data. Obtenha resultados que fazem sentido para seu país e cidade específicos, com contexto financeiro regional incluído."
        }
      ]
    },
    fr: {
      title: "Calculatrices Financières Gratuites pour une Gestion Intelligente de l\'Argent",
      sections: [
        {
          heading: "Pourquoi Utiliser Nos Calculatrices Financières?",
          icon: "Target",
          text: "Nos calculatrices fournissent des réponses instantanées et précises aux questions financières critiques. Que vous planifiez votre budget ou gérez vos dettes, obtenez des informations claires en quelques secondes avec nos outils faciles à utiliser conçus pour la planification financière du monde réel."
        },
        {
          heading: "Avantages de la Calculatrice de Durée de l\'Argent",
          icon: "Clock",
          text: "Comprenez exactement combien de temps dureront vos économies en fonction de vos dépenses actuelles. Obtenez des indicateurs de statut visuels montrant si vous êtes dans une position financière sûre, d'avertissement ou critique. Planifiez en toute confiance en connaissant votre marge financière exacte."
        },
        {
          heading: "Avantages de la Calculatrice de Liberté de Dette",
          icon: "TrendingDown",
          text: "Calculez votre date exacte sans dette et voyez votre statut de progression. Sachez si vous êtes sur la bonne voie, en difficulté ou si vous devez ajuster votre stratégie de paiement. Prenez le contrôle de votre dette avec des calendriers clairs et exploitables."
        },
        {
          heading: "Localisé pour Votre Région",
          icon: "Globe",
          text: "Tous les calculs s'adaptent automatiquement à votre devise locale, au formatage des nombres et aux conventions de date. Obtenez des résultats qui ont du sens pour votre pays et votre ville spécifiques, avec un contexte financier régional inclus."
        }
      ]
    },
    de: {
      title: "Kostenlose Finanzrechner für Intelligentes Geldmanagement",
      sections: [
        {
          heading: "Warum Unsere Finanzrechner Verwenden?",
          icon: "Target",
          text: "Unsere Rechner liefern sofortige, genaue Antworten auf kritische Finanzfragen. Ob Sie Ihr Budget planen oder Schulden verwalten, erhalten Sie klare Einblicke in Sekunden mit unseren benutzerfreundlichen Tools, die für die reale Finanzplanung entwickelt wurden."
        },
        {
          heading: "Vorteile des Gelddauer-Rechners",
          icon: "Clock",
          text: "Verstehen Sie genau, wie lange Ihre Ersparnisse basierend auf Ihren aktuellen Ausgaben reichen werden. Erhalten Sie visuelle Statusindikatoren, die zeigen, ob Sie sich in einer sicheren, Warn- oder kritischen Finanzposition befinden. Planen Sie voraus mit Vertrauen, indem Sie Ihre genaue finanzielle Reichweite kennen."
        },
        {
          heading: "Vorteile des Schuldenfreiheits-Rechners",
          icon: "TrendingDown",
          text: "Berechnen Sie Ihr genaues schuldenfreies Datum und sehen Sie Ihren Fortschrittsstatus. Wissen Sie, ob Sie auf Kurs sind, kämpfen oder Ihre Zahlungsstrategie anpassen müssen. Übernehmen Sie die Kontrolle über Ihre Schulden mit klaren, umsetzbaren Zeitplänen."
        },
        {
          heading: "Lokalisiert für Ihre Region",
          icon: "Globe",
          text: "Alle Berechnungen passen sich automatisch an Ihre lokale Währung, Zahlenformatierung und Datumskonventionen an. Erhalten Sie Ergebnisse, die für Ihr spezifisches Land und Ihre Stadt sinnvoll sind, mit regionalem Finanzkontext."
        }
      ]
    }
  };

  const data = content?.[language] || content?.en;

  return (
    <div className="bg-background py-8 md:py-12">
      <div className="max-w-4xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-semibold text-foreground mb-8 text-center">
          {data?.title}
        </h2>

        <div className="space-y-6">
          {data?.sections?.map((section, index) => (
            <div 
              key={index}
              className="bg-card border border-border rounded-lg p-6 md:p-8"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name={section?.icon} size={20} color="var(--color-primary)" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                    {section?.heading}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {section?.text}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SEOContent;